import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import { authAPI, setLogoutHandler } from '../services/api';

// Initial state
const initialState = {
  user: null,
  token: null,
  loading: true,
  error: null,
  errorCode: null,
};

// Auth actions
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_LOADING: 'SET_LOADING',
};

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
        error: null,
      };
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: action.payload.message || action.payload,
        errorCode: action.payload.code || null,
      };
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: null,
      };
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
        errorCode: null,
      };
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');

      if (token && user) {
        try {
          // Verify token is still valid
          const response = await authAPI.getMe();
          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: {
              user: response.data.user,
              token,
            },
          });
        } catch (error) {
          // Token is invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Register logout handler with API service for automatic session clearing
  useEffect(() => {
    setLogoutHandler(() => {
      // Clear storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Update AuthContext state
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    });
    
    // Cleanup: remove handler when component unmounts
    return () => {
      setLogoutHandler(null);
    };
  }, [dispatch]);

  // Login function
  const login = useCallback(async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const response = await authAPI.login(credentials);
      const { user, token } = response.data;

      // Store in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, token },
      });

      return { success: true };
    } catch (error) {
      const errorCode = error.response?.data?.code;
      const errorMessage = error.response?.data?.message || 'Login failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { message: errorMessage, code: errorCode },
      });
      return { success: false, error: errorMessage, code: errorCode };
    }
  }, []);

  // Register function
  const register = useCallback(async (userData) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      await authAPI.register(userData);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      // Ignore logout errors
    }

    // Clear storage
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  }, []);

  // Change password function
  const changePassword = useCallback(async (passwords) => {
    try {
      await authAPI.changePassword(passwords);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Password change failed';
      return { success: false, error: errorMessage };
    }
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  }, []);

  // Check if user has permission
  const hasPermission = useCallback((requiredRoles = []) => {
    if (!state.user) return false;
    if (requiredRoles.length === 0) return true;
    return requiredRoles.includes(state.user.role);
  }, [state.user]);

  // Check if user is admin
  const isAdmin = useCallback(() => state.user?.role === 'ADMIN', [state.user]);

  // Check if user is manager or admin (case-insensitive for role from API)
  const isManagerOrAdmin = useCallback(() => {
    const role = (state.user?.role || '').toString().toUpperCase();
    return ['ADMIN', 'MANAGER', 'SUPER'].includes(role);
  }, [state.user]);

  const isManager = useCallback(() => state.user?.role === 'MANAGER', [state.user]);

  // Check if user is client (CLIENT role)
  const isOperator = useCallback(() => state.user?.role === 'CLIENT', [state.user]);

  // Check if user is client login
  const isClient = useCallback(() => state.user?.role === 'CLIENT', [state.user]);

  // Check if user is driver
  const isDriver = useCallback(() => state.user?.role === 'DRIVER', [state.user]);

  const isPeseur = useCallback(() => state.user?.role === 'PESEUR', [state.user]);
  
  const isAccountant = useCallback(() => state.user?.role === 'ACCOUNTANT', [state.user]);
  
  const isSuper = useCallback(() => state.user?.role === 'SUPER', [state.user]);
  
  const isAccountantOrAdmin = useCallback(() => ['ADMIN', 'ACCOUNTANT'].includes(state.user?.role), [state.user]);

  // Get client user's or client login's assigned client
  const getOperatorClient = useCallback(() => {
    return state.user?.role === 'CLIENT' ? state.user.assignedClient : null;
  }, [state.user]);

  /**
   * Trucks for the logged-in driver: legacy User↔Truck (assignedTrucks) and Driver entity (driverProfile.truckDrivers).
   */
  const resolvedDriverTrucks = useMemo(() => {
    const u = state.user;
    if (!u || u.role !== 'DRIVER') return [];
    const byId = new Map();
    const merge = (id, partial) => {
      if (!id) return;
      const prev = byId.get(id) || {};
      byId.set(id, { ...prev, ...partial, id });
    };
    for (const row of u.assignedTrucks || []) {
      const t = row?.truck;
      if (t?.id) merge(t.id, t);
      else if (row?.truckId) merge(row.truckId, { id: row.truckId, plateNumber: null });
    }
    for (const row of u.driverProfile?.truckDrivers || []) {
      const t = row?.truck;
      if (t?.id) merge(t.id, t);
      else if (row?.truckId) merge(row.truckId, { id: row.truckId, plateNumber: null });
    }
    if (u.assignedTruck?.id) merge(u.assignedTruck.id, u.assignedTruck);
    return [...byId.values()];
  }, [state.user]);

  const getDriverTrucks = useCallback(() => resolvedDriverTrucks, [resolvedDriverTrucks]);

  const getDriverTruck = useCallback(() => resolvedDriverTrucks[0] || null, [resolvedDriverTrucks]);

  // Get all peseur's assigned weighbridges
  const getPeseurWeighbridges = useCallback(() => {
    if (state.user?.role !== 'PESEUR') return [];
    return state.user.peseurWeighbridges?.map(pw => pw.weighbridge).filter(Boolean) || [];
  }, [state.user]);

  // Get peseur's primary assigned weighbridge (first one for backward compat)
  const getPeseurWeighbridge = useCallback(() => {
    const wbs = state.user?.role === 'PESEUR'
      ? (state.user.peseurWeighbridges?.map(pw => pw.weighbridge).filter(Boolean) || [])
      : [];
    return wbs.length > 0 ? wbs[0] : null;
  }, [state.user]);

  const value = {
    ...state,
    login,
    register,
    logout,
    changePassword,
    clearError,
    hasPermission,
    isAdmin,
    isManagerOrAdmin,
    isManager,
    isOperator,
    isClient,
    isDriver,
    isPeseur,
    isAccountant,
    isSuper,
    isAccountantOrAdmin,
    getOperatorClient,
    getDriverTruck,
    getDriverTrucks,
    getPeseurWeighbridge,
    getPeseurWeighbridges,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;